import { useCallback } from 'react';
import { useUserActionMutation } from './useUserActionMutation';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { usePreviewMode } from '../contexts/PreviewModeContext';

export enum PracticeProblemUserActionType {
  STARTED = 'ActionTypeNode:9',
  RESUMED = 'ActionTypeNode:10',
  RESET = 'ActionTypeNode:11',
}

const actionTypeDescriptions: Record<PracticeProblemUserActionType, string> = {
  [PracticeProblemUserActionType.STARTED]: 'Practice Problems Started',
  [PracticeProblemUserActionType.RESUMED]: 'Practice Problems Resumed',
  [PracticeProblemUserActionType.RESET]: 'Practice Problems Reset',
};

export const usePracticeProblemUserActionSave = () => {
  const userRedux = useAppSelector(selectUser);
  const previewMode = usePreviewMode();
  const [commitMutation] = useUserActionMutation();

  const logPracticeProblemUserAction = useCallback(
    ({
      actionType,
      skillId,
      practiceProblemsSessionId,
    }: {
      actionType: PracticeProblemUserActionType;
      skillId: string;
      practiceProblemsSessionId: string;
    }) => {
      if (previewMode || !userRedux?.id) return;
      const description = actionTypeDescriptions[actionType] ?? 'Practice Problems Action';
      commitMutation({
        variables: {
          input: {
            userAction: {
              userId: `UserNode:${userRedux?.id ?? ''}`,
              actionTypeId: `${actionType}`,
              skillId: skillId ?? '',
              timeStamp: new Date().toISOString(),
              description: `${description}`,
              practiceProblemsSessionId: practiceProblemsSessionId ?? '',
            },
          },
        },
      });
    },
    [commitMutation, previewMode, userRedux?.id],
  );

  return {
    logPracticeProblemUserAction,
    logStarted: (skillId: string, practiceProblemsSessionId: string) =>
      logPracticeProblemUserAction({
        actionType: PracticeProblemUserActionType.STARTED,
        skillId,
        practiceProblemsSessionId,
      }),
    logResumed: (skillId: string, practiceProblemsSessionId: string) =>
      logPracticeProblemUserAction({
        actionType: PracticeProblemUserActionType.RESUMED,
        skillId,
        practiceProblemsSessionId,
      }),
    logReset: (skillId: string, practiceProblemsSessionId: string) =>
      logPracticeProblemUserAction({
        actionType: PracticeProblemUserActionType.RESET,
        skillId,
        practiceProblemsSessionId,
      }),
  };
};
